/* eslint-disable @typescript-eslint/no-explicit-any */
import { PiSparkleBold } from "react-icons/pi";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ScrollArea,
  ScrollBar,
} from "../../../../../../shadcnComponents/ui/scroll-area";

import { useToast } from "../../../../../../shadcnComponents/ui/use-toast";
import mixpanel from "mixpanel-browser";
import {
  AnalyzedPostSectionSkeleton,
  CollabCard,
  SectionCard,
} from "../DashboardAtoms";
import { PromotionalPostCheckForm } from "../DashboardMolecules";

const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

export const AiAnalysisCollabCheck = () => {
  const [promoPost, setPromoPost] = useState([]);
  const [masterGoals, setMasterGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const getPromoPost = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${host}/influencer/getAnalyzedPromotionalPosts`,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      if (res?.data?.success) {
        setPromoPost(res?.data?.posts);
        setMasterGoals(res?.data?.kpis);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    getPromoPost();
  }, []);

  const saveData = async (id, data) => {
    try {
      const res = await axios.post(
        `${host}/influencer/submitPromoCheck/${id}`,
        data,
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );
      if (res?.data?.success) {
        return { success: true, data: res?.data };
      }
    } catch (error) {
      console.error(error);
      return { success: false };
    }
  };

  const handleSubmitAnswer = async ({ post, answers }) => {
    let isCollab = false;
    answers
      ?.find((answers) => answers?.questionId === 1)
      ?.selectedOptions?.forEach((option) => {
        if (option?.key === "yes") {
          isCollab = true;
        }
      });

    let collab: any = undefined;
    if (isCollab) {
      collab = {
        companyName: answers
          ?.find((answers) => answers?.questionId === 2)
          ?.selectedOptions?.map((option) => option?.value)?.[0],
        collabType: answers
          ?.find((answers) => answers?.questionId === 3)
          ?.selectedOptions?.map((option) => option?.value)?.[0],
        collabEarning: answers?.find((answers) => answers?.questionId === 4)
          ?.selectedOptions[0]?.key,
        goals: answers
          ?.find((answers) => answers?.questionId === 5)
          ?.selectedOptions?.map((option) => option?.key),
        postLink: post?.postFeedUrl,
      };
    }
    // console.log(data);

    mixpanel.track("Submitted Promotional Post Analysis Check", {
      postLink: post?.postFeedUrl,
      isCollab,
    });

    const res = await saveData(post?.id, {
      data: {
        isCollab,
        collab,
      },
    });

    setTimeout(() => {
      if (res?.success) {
        promoPost?.splice(
          promoPost?.findIndex((item) => item?.id === post?.id),
          1
        );
        toast({
          title: "Collaboration saved successfully",
        });
      } else {
        toast({
          title: "Failed to save data ; refresh & try again",
          variant: "destructive",
        });
      }
    }, 1000);
  };

  return (
    <>
      {loading && <AnalyzedPostSectionSkeleton />}
      {!loading && promoPost?.length > 0 && (
        <div className="text-white">
          <SectionCard
            title="Are These Your Past Collaborations?"
            icon={<PiSparkleBold size={24} color="#DC2627" />}
            description="Our Al flagged these posts as potential brand collaborations. Please confirm or deny to show your updated & accurate profile to brands"
          >
            <ScrollArea className="pb-4">
              <div className="flex gap-4 max-w-screen-sm">
                {promoPost?.map((post, index) => {
                  const Q2Options = Array.from(
                    new Set([
                      ...(post?.primaryPromotionalCompany?.slice(0, 2) || []),
                      ...(post?.secondaryPromotionalCompany?.slice(0, 1) || []),
                    ])
                  ).map((company) => ({
                    key: company?.trim()?.toLowerCase(),
                    value: company?.trim(),
                  }));

                  const suggestGoals = Array.from(
                    new Set(post?.keyMetricsForCollaboration || [])
                  ).map((kpi: string) => ({
                    key: kpi?.trim()?.toLowerCase(),
                    value: kpi?.trim(),
                  }));

                  const Q4Options = [];

                  suggestGoals?.forEach((suggestedGoal) => {
                    const matchingGoal = masterGoals?.find(
                      (goal) =>
                        goal?.value?.trim()?.toLowerCase() ===
                        suggestedGoal?.key?.trim()?.toLowerCase()
                    );
                    if (matchingGoal) {
                      Q4Options.push({
                        key: matchingGoal?.key,
                        value: matchingGoal?.value,
                      });
                    }
                  });

                  const remainingGoals = masterGoals?.filter(
                    (goal) =>
                      !Q4Options.some((option) => option?.key === goal?.key)
                  );

                  Q4Options.push(...remainingGoals);

                  const QuestionsList: QuestionsType[] = [
                    {
                      id: 1,
                      title: "Was it a collaboration?",
                      options: [
                        {
                          key: "no",
                          value: "No",
                        },
                        {
                          key: "yes",
                          value: "Yes",
                          isSuggested: true,
                        },
                      ],
                      selectedOptions: [],
                      selectionType: "single",
                    },
                    {
                      id: 2,
                      title: "Who was this collab with?",
                      options: [...Q2Options],
                      selectedOptions: [],
                      selectionType: "single",
                    },
                    {
                      id: 3,
                      title: "What type of collab it was?",
                      options: [
                        {
                          key: "paid",
                          value: "Paid",
                        },
                        {
                          key: "barter",
                          value: "Barter",
                        },
                        {
                          key: "free",
                          value: "Free",
                        },
                      ],
                      selectedOptions: [],
                      selectionType: "single",
                      tooltip:
                        "It's only for reference & is visible only to you",
                    },
                    {
                      id: 4,
                      title: "How much did you get paid?",
                      options: [],
                      selectedOptions: [],
                      selectionType: "single",
                      tooltip:
                        "It's only for reference & is visible only to you",
                      isInput: true,
                      hide: false,
                    },
                    {
                      id: 5,
                      title: "Purpose of the collab?",
                      options: [...Q4Options],
                      selectedOptions: [],
                      selectionType: "multiple",
                    },
                  ];

                  return (
                    <div key={index} className="w-[300px] h-full">
                      <CollabCard
                        text={post?.postDescription}
                        postFeedUrl={post?.postFeedUrl}
                        metrics={post?.publicMetrics}
                      >
                        <PromotionalPostCheckForm
                          questions={QuestionsList}
                          handleSubmitAnswer={async ({ answers }) =>
                            await handleSubmitAnswer({
                              answers: answers,
                              post: post,
                            })
                          }
                        />
                      </CollabCard>
                    </div>
                  );
                })}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </SectionCard>
        </div>
      )}
    </>
  );
};
